import {announce} from '@github-ui/aria-live'
import {hpcAttrs} from '@github-ui/hpc'
import {
  IpmErrorBoundary,
  reportIpmAction,
  reportIpmDismiss,
  reportIpmVisible,
  type IpmMetricTags,
  useIpmRenderTiming,
} from '@github-ui/ipm-telemetry'
import {verifiedFetch, reactFetchJSON} from '@github-ui/verified-fetch'
import {XIcon} from '@primer/octicons-react'
import {Button, Flash, IconButton, Link} from '@primer/react'
import {type MouseEvent as ReactMouseEvent, type ReactNode, Profiler, useEffect, useMemo, useState} from 'react'
import type {BannerSegment, BannerAnalytics} from '@github-ui/code-view-types'
import styles from './BannerNudgeCard.module.css'
import {CommandPicker, type InstallationMethod, type CommandPickerFooter} from './CommandPicker'

// Announced to screen readers (and shown inline) when a POST-action CTA fails.
const CTA_POST_ERROR_MESSAGE = 'Something went wrong. Please try again.'
// Announced by the CTA button while its POST request is in flight.
const CTA_POST_LOADING_ANNOUNCEMENT = 'Submitting request'

export interface BannerNudgeAnalytics extends BannerAnalytics {
  ctaAction: string
  entityId?: number
}

export interface BannerNudgeCardProps {
  title: string
  segments: BannerSegment[]
  icon: ReactNode
  ctaTitle?: string
  ctaHref?: string
  /**
   * POST endpoint for a CTA that performs an action rather than navigating
   * (e.g. the GHEC `raf_cb` "Ask enterprise owners for access" CTA, which
   * creates a member feature request via
   * `POST /organizations/:org/settings/member_feature_requests`). When set, the
   * CTA renders as a button that `verifiedFetch`-POSTs `ctaPostBody` (as JSON)
   * instead of an `<a href>` link, mirroring the dismiss control. Takes
   * precedence over `ctaHref` when both are provided. On success the button
   * stays mounted (so keyboard focus is preserved), becomes `aria-disabled`,
   * and swaps its label to the `ctaRequestedLabel` confirmation.
   */
  ctaPostPath?: string
  /**
   * JSON body sent with the `ctaPostPath` POST (e.g.
   * `{feature: 'copilot_for_business'}`). Ignored unless `ctaPostPath` is set.
   */
  ctaPostBody?: Record<string, string>
  /**
   * Confirmation label shown in place of the CTA after a successful
   * `ctaPostPath` POST. Defaults to `'Requested'`. Ignored unless `ctaPostPath`
   * is set.
   */
  ctaRequestedLabel?: string
  ctaPrimary?: boolean
  stacked?: boolean
  /**
   * Visual layout style.
   * - `settings` (default): renders as a `Box-header`-style row designed to live inside
   *   a parent `.Box` wrapper (its left/right borders rely on the parent's border).
   *   Used for settings/policy pages where multiple cards stack together.
   *   Renders the title as `<h2>` to fit the typical settings-page heading hierarchy
   *   (page-level `<h1>` followed by `<h2>` sections).
   * - `dashboard`: renders as a standalone bordered card with its own 4-sided border,
   *   padding, and background. Use when the nudge is rendered on its own (e.g. the
   *   dashboard) without a parent `.Box` wrapper. Renders the title as `<h3>` to match
   *   the original ERB markup, the existing `.ipm-nudge-content h3` styles in
   *   `nudges.scss`, and a dashboard hierarchy of `<h1>` → `<h2>` section → `<h3>` nudge.
   */
  displayStyle?: 'settings' | 'dashboard'
  /**
   * POST endpoint for the dismiss action. Required to render the dismiss X.
   * Omit (or pass `null`/`undefined`) to suppress the dismiss control entirely
   * — used by nudges that must not offer a global dismiss (e.g. the
   * `raf_cb` org-settings nudge once Copilot Business is enabled; the admin
   * must resolve each pending member request individually rather than hiding
   * the whole card).
   */
  dismissPath?: string | null
  analytics: BannerNudgeAnalytics
  testSelector: string
  installationMethods?: InstallationMethod[]
  defaultInstallationMethod?: string
  footer?: CommandPickerFooter
  /**
   * defaults to `true`.
   */
  rounded?: boolean
}

function getSegmentKey(segment: BannerSegment, index: number): string {
  return `${segment.type}-${index}-${segment.content.slice(0, 20)}`
}

export function BannerNudgeCard({
  title,
  segments,
  icon,
  ctaTitle,
  ctaHref,
  ctaPostPath,
  ctaPostBody,
  ctaRequestedLabel = 'Requested',
  ctaPrimary = true,
  stacked = false,
  displayStyle = 'settings',
  dismissPath,
  analytics,
  testSelector,
  installationMethods,
  defaultInstallationMethod,
  footer,
  rounded = true,
}: BannerNudgeCardProps) {
  const [hidden, setHidden] = useState(false)
  const [ctaRequested, setCtaRequested] = useState(false)
  const [ctaPending, setCtaPending] = useState(false)
  const [ctaError, setCtaError] = useState<string | null>(null)
  const telemetryTags = useMemo<IpmMetricTags>(
    () => ({
      package: 'in-product-targeting',
      component: 'BannerNudgeCard',
      surface: 'banner',
      display_location: analytics.location,
      category: analytics.category,
      nudge_id: testSelector,
      variant: displayStyle,
    }),
    [analytics.category, analytics.location, displayStyle, testSelector],
  )
  const renderTiming = useIpmRenderTiming(telemetryTags)

  useEffect(() => {
    reportIpmVisible(telemetryTags)
  }, [telemetryTags])

  const handleDismiss = async () => {
    reportIpmDismiss(telemetryTags)
    if (!dismissPath) {
      setHidden(true)
      return
    }
    try {
      await verifiedFetch(dismissPath, {method: 'POST'})
    } catch {
      reportIpmAction('error', telemetryTags, {samplingProbability: 1})
      // Silently handle network errors to maintain good UX
    } finally {
      setHidden(true)
    }
  }

  // POST-action CTA (e.g. "Ask enterprise owners for access"). Mirrors
  // handleDismiss: reports the click, POSTs via verifiedFetch, and reflects the
  // result in local state. On success the button stays mounted, becomes
  // `aria-disabled`, and swaps its label to the `ctaRequestedLabel`
  // confirmation; on failure the button re-enables so the admin can retry.
  const handleCtaPost = async () => {
    if (!ctaPostPath || ctaPending || ctaRequested) {
      return
    }
    reportIpmAction('cta_click', telemetryTags)
    setCtaError(null)
    setCtaPending(true)
    try {
      // reactFetchJSON adds the GitHub-Is-React header for Datadog request
      // attribution and handles JSON headers + body serialization.
      const response = await reactFetchJSON(ctaPostPath, {
        method: 'POST',
        body: ctaPostBody ?? {},
      })
      if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}`)
      }
      setCtaRequested(true)
      // Announce the confirmation politely so screen reader users are informed
      // even though the button text already changed.
      announce(ctaRequestedLabel)
    } catch {
      reportIpmAction('error', telemetryTags, {samplingProbability: 1})
      // Surface the failure both visually (Flash) and to assistive tech so the
      // admin knows to retry (WCAG 4.1.3 Status Messages).
      setCtaError(CTA_POST_ERROR_MESSAGE)
      announce(CTA_POST_ERROR_MESSAGE, {assertive: true})
    } finally {
      setCtaPending(false)
    }
  }

  const handleInactiveCtaClickCapture = (event: ReactMouseEvent<HTMLDivElement>) => {
    if (!ctaPending && !ctaRequested) {
      return
    }

    event.preventDefault()
    event.stopPropagation()
  }

  if (hidden) {
    return null
  }

  const isDashboard = displayStyle === 'dashboard'
  // The `settings` display style uses `Box-header`, which has negative left/right/top
  // margins designed to overlap a parent `.Box` border, so we inject the bottom border
  // + top corner radii inline when the card isn't stacked. The `dashboard` display
  // style's CSS (`.ipm-nudge-box.dashboard` in nudges.scss) already supplies a full
  // standalone border, padding, and background, so no inline styles or `Box-header`
  // class are needed.
  const containerClassName = [
    isDashboard ? 'ipm-nudge-box dashboard' : 'ipm-nudge-box settings Box-header',
    !rounded && styles.notRounded,
  ]
    .filter(Boolean)
    .join(' ')
  const containerStyle =
    !isDashboard && !stacked
      ? {
          borderBottom: 'var(--borderWidth-thin) solid var(--borderColor-default)',
          ...(rounded && {
            borderTopLeftRadius: 'var(--borderRadius-medium)',
            borderTopRightRadius: 'var(--borderRadius-medium)',
          }),
        }
      : undefined

  const card = (
    <>
      <span data-test-selector={testSelector} />
      <div
        className={containerClassName}
        style={containerStyle}
        {...hpcAttrs()}
        data-testid="task-nudge-card"
        data-analytics-visible={JSON.stringify({
          category: analytics.category,
          action: 'visible',
          label: `location:${analytics.location}`,
        })}
      >
        <div className={styles.IconWrapper} aria-hidden="true">
          {icon}
        </div>

        {/* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */}
        <div className="ipm-nudge-content">
          {isDashboard ? <h3>{title}</h3> : <h2>{title}</h2>}
          <p>
            {segments.map((segment, index) =>
              segment.type === 'link' && 'href' in segment ? (
                <Link key={getSegmentKey(segment, index)} href={segment.href} className="Link--inTextBlock">
                  {segment.content}
                </Link>
              ) : (
                <span key={getSegmentKey(segment, index)}>{segment.content}</span>
              ),
            )}
          </p>
          {installationMethods && installationMethods.length > 0 && (
            <CommandPicker
              installationMethods={installationMethods}
              defaultInstallationMethod={defaultInstallationMethod}
              footer={footer}
              telemetryTags={telemetryTags}
              analytics={{
                category: analytics.category,
                label: `location:${analytics.location}`,
              }}
            />
          )}
          {ctaError ? (
            <Flash variant="danger" className="mt-2" data-test-selector={`${testSelector}-cta-error`}>
              {ctaError}
            </Flash>
          ) : null}
        </div>

        {ctaTitle && ctaPostPath && !installationMethods?.length ? (
          // Keep `ipm-nudge-cta` on an outer wrapper so it stays the direct
          // flex child of `.ipm-nudge-box` even when Primer's `Button loading`
          // nests the button inside its own `<div data-loading-wrapper>`. The
          // layout/alignment/responsive rules (nudges.scss) target this class.
          /* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */
          <div className="ipm-nudge-cta" onClickCapture={handleInactiveCtaClickCapture}>
            {/* Keep the Button in the DOM on success so keyboard focus is not lost when the
                request completes. The `inactive` prop applies Primer's muted styling so the
                completed CTA also looks disabled to sighted users, while `aria-disabled`
                exposes the state to assistive tech without removing it from the tab sequence;
                the handler also guards against re-submission. */}
            <Button
              type="button"
              variant={ctaPrimary && !ctaRequested ? 'primary' : 'default'}
              size="small"
              loading={ctaPending}
              loadingAnnouncement={CTA_POST_LOADING_ANNOUNCEMENT}
              onClick={handleCtaPost}
              inactive={ctaRequested || undefined}
              aria-disabled={ctaPending || ctaRequested || undefined}
              data-test-selector={testSelector}
              data-analytics-event={JSON.stringify({
                category: analytics.category,
                action: analytics.ctaAction,
                label: `location:${analytics.location}`,
              })}
            >
              {ctaRequested ? ctaRequestedLabel : ctaTitle}
            </Button>
          </div>
        ) : (
          ctaTitle &&
          ctaHref &&
          !installationMethods?.length && (
            <a
              href={ctaHref}
              onClick={() => reportIpmAction('cta_click', telemetryTags)}
              /* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */
              className={`ipm-nudge-cta btn btn-sm${ctaPrimary ? ' btn-primary' : ''}`}
              data-test-selector={testSelector}
              data-analytics-event={JSON.stringify({
                category: analytics.category,
                action: analytics.ctaAction,
                label: `location:${analytics.location}`,
              })}
            >
              {ctaTitle}
            </a>
          )
        )}

        {dismissPath && (
          /* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */
          <div className="ipm-nudge-dismiss">
            <IconButton
              icon={XIcon}
              variant="invisible"
              aria-label="Dismiss"
              onClick={handleDismiss}
              data-analytics-event={JSON.stringify({
                category: analytics.category,
                action: 'dismiss',
                label: `location:${analytics.location}`,
              })}
            />
          </div>
        )}
      </div>
    </>
  )

  const content = !renderTiming.isEnabled ? (
    card
  ) : (
    <Profiler id="BannerNudgeCard" onRender={renderTiming.onRender}>
      {card}
    </Profiler>
  )

  return (
    <IpmErrorBoundary tags={telemetryTags} boundaryName="BannerNudgeCard">
      {content}
    </IpmErrorBoundary>
  )
}
